import { Fragment, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, ApiError } from '../api.ts'
import { useAuth } from '../auth/AuthContext.tsx'
import { money, unitStatusLabel, ageOnLot } from '../format.ts'

interface Unit {
  id: string
  machineId: string
  serialNumber: string | null
  productionYear: number | null
  status: string
  receivedAt: string | null
  soldAt: string | null
  warrantyStart: string | null
  warrantyMonths: number | null
  warrantyEnd: string | null
  supplierWarrantyEnd: string | null
  supplierWarrantyRegistered: boolean
  notes: string | null
  model: string
  catalogCode: string
  techSpec: { key: string; value: string }[] | null
  manufacturerName: string
  isReserved: boolean
  reservationExpired: boolean
  isSold: boolean
  isAvailable: boolean
  agedOnYard: boolean
  reservationConflict: boolean
  reservation: { id: string; customerName: string | null; expiresAt: string | null; reason: string | null; reservedByCode: string | null } | null
  order: { orderId: string; orderNumber: string; customerName: string } | null
  cost?: { landedCostPln: string | null; purchaseInvoiceRef: string | null; marginPln: string | null } | null
}

export function UnitPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const isOwner = user?.role === 'wlasciciel'
  // Warehouse writes belong to sales and the owner (grants 0014); service reads.
  const mozeWydawac = isOwner || user?.role === 'handlowiec'
  const [u, setU] = useState<Unit | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  function reload() {
    if (!id) return
    api
      .get<{ unit: Unit }>(`/api/units/${id}`)
      .then((r) => setU(r.unit))
      .catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
  }
  useEffect(reload, [id])

  async function release() {
    if (!u?.reservation) return
    setBusy(true)
    try {
      await api.post(`/api/reservations/${u.reservation.id}/release`)
      reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    } finally {
      setBusy(false)
    }
  }

  if (error && !u) return <div className="p-6 text-red-700">{error}</div>
  if (!u) return <div className="p-6 text-slate-400">Ładowanie…</div>

  const wydany = u.status === 'handed_over'
  const age = ageOnLot(u.receivedAt)

  return (
    <div className="p-6 max-w-3xl">
      <Link to="/magazyn" className="text-sm text-slate-500 hover:text-slate-700">
        ← Magazyn
      </Link>

      <div className="mt-2 mb-5">
        <h1 className="text-xl font-bold text-slate-800 font-mono">
          {u.serialNumber ?? '— bez numeru seryjnego —'}
          <span className="ml-3 text-sm font-sans font-normal text-slate-500">{unitStatusLabel(u.status)}</span>
        </h1>
        <div className="text-sm text-slate-500 mt-0.5">
          {u.model} · {u.manufacturerName} · {u.catalogCode}
          {u.productionYear && ` · rok ${u.productionYear}`}
        </div>
      </div>

      {error && <div role="alert" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

      {/* Warranty — the big client-facing indicator (SPEC 5.5) */}
      {wydany && u.warrantyEnd && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-lg font-bold text-green-800">GWARANCJA DO {new Date(u.warrantyEnd).toLocaleDateString('pl-PL')}</div>
          <div className="text-xs text-green-700 mt-0.5">
            Wydany {u.soldAt && new Date(u.soldAt).toLocaleDateString('pl-PL')}
            {u.order && <> · {u.order.customerName}</>}
            {u.warrantyMonths != null && <> · {u.warrantyMonths} mies.</>}
          </div>
          {u.supplierWarrantyEnd && new Date(u.warrantyEnd) > new Date(u.supplierWarrantyEnd) && (
            <div className="text-xs text-amber-700 mt-1">
              ⚠ Nasza gwarancja jest dłuższa niż gwarancja producenta (do{' '}
              {new Date(u.supplierWarrantyEnd).toLocaleDateString('pl-PL')}) — naprawę po tej dacie pokryjemy sami.
            </div>
          )}
        </div>
      )}

      {/* Status boxes */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white border border-slate-200 rounded-lg p-3 text-sm">
          <div className="text-xs uppercase text-slate-400 mb-1">Na placu</div>
          {u.status === 'in_stock' ? (
            <span className={age.overdue ? 'text-red-700 font-semibold' : 'text-slate-700'}>
              {age.label}
              {age.overdue && ' — zalega'}
            </span>
          ) : (
            <span className="text-slate-400">{unitStatusLabel(u.status)}</span>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-3 text-sm">
          <div className="text-xs uppercase text-slate-400 mb-1">Dostępność</div>
          {u.isAvailable && <span className="text-green-700">wolny do sprzedania</span>}
          {u.isSold && u.order ? (
            <span className={wydany ? 'text-slate-700' : 'text-blue-700'}>
              {wydany ? 'wydany:' : 'sprzedany:'}{' '}
              <Link to={`/zamowienia/${u.order.orderId}`} className="underline">
                {u.order.orderNumber}
              </Link>{' '}
              · {u.order.customerName}
            </span>
          ) : null}
          {u.isReserved && !u.isSold && <span className={u.reservationExpired ? 'text-red-700' : 'text-amber-700'}>zarezerwowany</span>}
        </div>
      </div>

      {/* Machine identity + technical spec (SPEC 4) */}
      <div className="mb-4 bg-white border border-slate-200 rounded-lg p-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-2">Specyfikacja</h2>
        <dl className="text-sm grid grid-cols-[10rem_1fr] gap-x-4 gap-y-1">
          <dt className="text-slate-500">Numer seryjny</dt>
          <dd className="font-mono text-slate-800">{u.serialNumber ?? '—'}</dd>
          <dt className="text-slate-500">Rok produkcji</dt>
          <dd className="text-slate-800">{u.productionYear ?? '—'}</dd>
          <dt className="text-slate-500">Model</dt>
          <dd className="text-slate-800">{u.model}</dd>
          <dt className="text-slate-500">Producent</dt>
          <dd className="text-slate-800">{u.manufacturerName}</dd>
          <dt className="text-slate-500">Kod katalogowy</dt>
          <dd className="font-mono text-slate-800">{u.catalogCode}</dd>
          {u.techSpec?.map((s) => (
            <Fragment key={s.key}>
              <dt className="text-slate-500">{s.key}</dt>
              <dd className="text-slate-800">{s.value}</dd>
            </Fragment>
          ))}
        </dl>
        {(!u.techSpec || u.techSpec.length === 0) && (
          <p className="text-xs text-slate-400 mt-2">Brak specyfikacji technicznej — uzupełnisz ją w karcie maszyny w katalogu.</p>
        )}
      </div>

      {/* Reservation */}
      {u.reservation && !wydany && (
        <div className={`mb-4 rounded-lg p-4 border ${u.reservationExpired ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="text-sm">
              <div className={u.reservationExpired ? 'text-red-800 font-medium' : 'text-amber-900 font-medium'}>
                {u.reservationExpired ? 'Rezerwacja wygasła' : 'Zarezerwowany'}
                {u.reservation.customerName ? ` dla ${u.reservation.customerName}` : ` — ${u.reservation.reason}`}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {u.reservation.expiresAt && `ważna do ${new Date(u.reservation.expiresAt).toLocaleDateString('pl-PL')}`}
                {u.reservation.reservedByCode && ` · ${u.reservation.reservedByCode}`}
              </div>
              {u.reservationExpired && <div className="text-xs text-red-700 mt-1">Zwolnij, jeśli klient zrezygnował — inaczej blokuje sprzedaż.</div>}
            </div>
            <button onClick={() => void release()} disabled={busy} className="text-xs border border-slate-300 text-slate-600 px-3 py-1.5 rounded-md hover:border-red-400 hover:text-red-700 shrink-0">
              Zwolnij
            </button>
          </div>
        </div>
      )}

      {u.reservationConflict && (
        <div className="mb-4 text-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          ⚠ Ten egzemplarz jest zarezerwowany dla jednego klienta, a sprzedany innemu. Sprawdź, komu ma trafić.
        </div>
      )}

      {/* Actions */}
      {!wydany && mozeWydawac && (
        <div className="flex gap-2 mb-4">
          {!u.isReserved && <ReserveButton unitId={u.id} onDone={reload} />}
          <HandoverButton unit={u} onDone={reload} />
        </div>
      )}

      {/* Owner-only: cost and real margin */}
      {isOwner && (
        <OwnerCost unitId={u.id} cost={u.cost ?? null} onDone={reload} />
      )}
    </div>
  )
}

// ── Reserve ──────────────────────────────────────────────────────────────────

function ReserveButton({ unitId, onDone }: { unitId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const [clients, setClients] = useState<{ id: string; name: string }[]>([])
  const [q, setQ] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [reason, setReason] = useState('')
  const [expires, setExpires] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || q.trim().length < 2) return
    const t = setTimeout(() => {
      api.get<{ clients: { id: string; name: string }[] }>(`/api/clients?search=${encodeURIComponent(q.trim())}`).then((r) => setClients(r.clients.slice(0, 6))).catch(() => {})
    }, 130)
    return () => clearTimeout(t)
  }, [q, open])

  async function submit() {
    setError(null)
    try {
      await api.post(`/api/units/${unitId}/reserve`, {
        customerId: customerId || undefined,
        reason: reason.trim() || undefined,
        expiresAt: expires ? new Date(expires).toISOString() : undefined,
      })
      setOpen(false)
      onDone()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-sm border border-slate-300 text-slate-700 px-4 py-2 rounded-md hover:border-brand-600">
        Zarezerwuj
      </button>
    )
  }
  return (
    <div className="w-full bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-sm font-medium text-slate-700 mb-2">Rezerwacja egzemplarza</div>
      <input value={q} onChange={(e) => { setQ(e.target.value); setCustomerId('') }} placeholder="Klient (albo zostaw puste na demo)" className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm mb-1" />
      {customerId ? (
        <div className="text-xs text-green-700 mb-2">wybrano: {clients.find((c) => c.id === customerId)?.name}</div>
      ) : (
        clients.length > 0 && (
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100 mb-2">
            {clients.map((c) => (
              <button key={c.id} onClick={() => { setCustomerId(c.id); setQ(c.name) }} className="w-full text-left px-3 py-1.5 hover:bg-brand-50 text-sm">
                {c.name}
              </button>
            ))}
          </div>
        )
      )}
      {!customerId && <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Powód (wymagany bez klienta, np. maszyna demo)" className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm mb-2" />}
      <label className="block text-xs text-slate-500 mb-2">
        Ważna do (opcjonalnie)
        <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="mt-0.5 block px-3 py-1.5 border border-slate-300 rounded-md text-sm" />
      </label>
      {error && <div className="text-sm text-red-700 mb-2">{error}</div>}
      <div className="flex justify-end gap-2">
        <button onClick={() => setOpen(false)} className="text-sm text-slate-500 px-3 py-1.5">Anuluj</button>
        <button onClick={() => void submit()} className="text-sm bg-brand-600 text-white px-4 py-1.5 rounded-md hover:bg-brand-700">Zarezerwuj</button>
      </div>
    </div>
  )
}

// ── Handover ─────────────────────────────────────────────────────────────────

function HandoverButton({ unit, onDone }: { unit: Unit; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const [months, setMonths] = useState('24')
  const [lines, setLines] = useState<{ id: string; orderNumber: string; customerName: string }[]>([])
  const [lineId, setLineId] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || unit.isSold) return
    api.get<{ lines: { id: string; orderNumber: string; customerName: string }[] }>(`/api/machines/${unit.machineId}/open-order-lines`).then((r) => setLines(r.lines)).catch(() => {})
  }, [open, unit.machineId, unit.isSold])

  async function submit() {
    setError(null)
    try {
      await api.post(`/api/units/${unit.id}/handover`, {
        warrantyMonths: Number(months),
        customerOrderItemId: lineId || undefined,
      })
      setOpen(false)
      onDone()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-sm bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700 font-medium">
        Wydaj klientowi
      </button>
    )
  }
  return (
    <div className="w-full bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-sm font-medium text-slate-700 mb-1">Wydanie klientowi</div>
      <p className="text-xs text-slate-400 mb-3">Ustawia datę sprzedaży i startuje gwarancję. Po wydaniu tych danych nie da się zmienić.</p>
      {!unit.isSold && (
        <label className="block text-xs text-slate-500 mb-2">
          Dla zamówienia
          {lines.length > 0 ? (
            <select value={lineId} onChange={(e) => setLineId(e.target.value)} className="mt-0.5 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
              <option value="">— wybierz —</option>
              {lines.map((l) => (
                <option key={l.id} value={l.id}>{l.orderNumber} · {l.customerName}</option>
              ))}
            </select>
          ) : (
            <div className="text-xs text-amber-700 mt-1">Brak otwartego zamówienia na tę maszynę — dowiąż egzemplarz do zamówienia przez przyjęcie.</div>
          )}
        </label>
      )}
      {unit.isSold && unit.order && <div className="text-xs text-slate-600 mb-2">dla {unit.order.customerName} ({unit.order.orderNumber})</div>}
      <label className="block text-xs text-slate-500 mb-3">
        Gwarancja (miesięcy)
        <input type="number" value={months} onChange={(e) => setMonths(e.target.value)} className="mt-0.5 w-28 block px-3 py-1.5 border border-slate-300 rounded-md text-sm" />
      </label>
      {error && <div className="text-sm text-red-700 mb-2">{error}</div>}
      <div className="flex justify-end gap-2">
        <button onClick={() => setOpen(false)} className="text-sm text-slate-500 px-3 py-1.5">Anuluj</button>
        <button
          onClick={() => void submit()}
          disabled={!unit.isSold && !lineId}
          className="text-sm bg-brand-600 text-white px-4 py-1.5 rounded-md hover:bg-brand-700 disabled:opacity-50"
        >
          Wydaj
        </button>
      </div>
    </div>
  )
}

// ── Owner: landed cost + margin ───────────────────────────────────────────────

function OwnerCost({ unitId, cost, onDone }: { unitId: string; cost: Unit['cost']; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const [landed, setLanded] = useState(cost?.landedCostPln ?? '')
  const [ref, setRef] = useState(cost?.purchaseInvoiceRef ?? '')
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setError(null)
    try {
      await api.put(`/api/units/${unitId}/cost`, {
        landedCostPln: landed ? Number(landed) : undefined,
        purchaseInvoiceRef: ref.trim() || undefined,
      })
      setOpen(false)
      onDone()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    }
  }

  return (
    <div className="mt-2 bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-semibold text-slate-700">Koszt i marża (tylko właściciel)</h2>
        {!open && (
          <button onClick={() => setOpen(true)} className="text-xs text-brand-600 hover:underline">
            {cost?.landedCostPln ? 'Popraw' : 'Wpisz koszt'}
          </button>
        )}
      </div>

      {!open ? (
        <div className="text-sm space-y-0.5">
          <div className="flex justify-between">
            <span className="text-slate-500">Koszt (landed)</span>
            <span className="tabular text-slate-800">{money(cost?.landedCostPln)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Marża rzeczywista</span>
            <span className={`tabular font-semibold ${cost?.marginPln && Number(cost.marginPln) > 0 ? 'text-green-700' : cost?.marginPln ? 'text-red-700' : 'text-slate-400'}`}>
              {cost?.marginPln ? money(cost.marginPln) : '—'}
            </span>
          </div>
          {cost?.purchaseInvoiceRef && <div className="text-xs text-slate-400">faktura {cost.purchaseInvoiceRef}</div>}
        </div>
      ) : (
        <div className="space-y-2">
          <label className="block text-xs text-slate-500">
            Koszt całkowity w PLN (landed: maszyna + transport + cło)
            <input type="number" value={landed} onChange={(e) => setLanded(e.target.value)} className="mt-0.5 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm" />
          </label>
          <label className="block text-xs text-slate-500">
            Numer faktury zakupu
            <input value={ref} onChange={(e) => setRef(e.target.value)} className="mt-0.5 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm" />
          </label>
          {error && <div className="text-sm text-red-700">{error}</div>}
          <div className="flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="text-sm text-slate-500 px-3 py-1.5">Anuluj</button>
            <button onClick={() => void submit()} className="text-sm bg-brand-600 text-white px-4 py-1.5 rounded-md hover:bg-brand-700">Zapisz</button>
          </div>
        </div>
      )}
    </div>
  )
}
